import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export const checkboxVariants = cva(
  [
    /* The pointer comes from the base layer, which knows to drop it when disabled. */
    'inline-flex shrink-0 items-center justify-center border-2 border-line bg-transparent text-white transition',
    'hover:border-ink-faint',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
    /* Root renders a <button>, but Base UI marks the disabled state too. */
    'data-disabled:pointer-events-none data-disabled:opacity-40',
  ],
  {
    variants: {
      size: {
        md: 'size-5',
        sm: 'size-4',
      },
      /*
       * A topic on a preparation is round, an action point is square. The
       * distinction is the prototype's and it carries meaning: the round ones
       * are things to raise in the conversation, the square ones are work
       * somebody owes. Shape lands after size so tailwind-merge resolves the
       * rounded-* conflict in its favour.
       */
      shape: {
        square: '',
        round: 'rounded-full',
      },
      color: {
        accent: 'data-checked:border-accent data-checked:bg-accent',
        primary:
          'data-checked:border-primary data-checked:bg-primary data-checked:ring-1 data-checked:ring-selected-edge',
      },
    },
    compoundVariants: [
      { size: 'md', shape: 'square', className: 'rounded-md' },
      { size: 'sm', shape: 'square', className: 'rounded' },
    ],
    defaultVariants: {
      size: 'md',
      shape: 'square',
      color: 'accent',
    },
  },
);

export interface CheckboxProps
  extends
    Omit<BaseCheckbox.Root.Props, 'style' | 'className' | 'color'>,
    VariantProps<typeof checkboxVariants> {
  className?: string;
}

/**
 * A box that is ticked or not — the gesture for "this is dealt with" on a
 * preparation topic or an action point.
 *
 * Not a Switch: a switch turns a setting on and leaves it on, while this one
 * marks a thing done. Same visual family, opposite meaning.
 */
export function Checkbox({ className, size, shape, color, ...props }: CheckboxProps) {
  return (
    <BaseCheckbox.Root
      data-slot='checkbox'
      className={cn(checkboxVariants({ size, shape, color }), className)}
      {...props}
    >
      <BaseCheckbox.Indicator
        data-slot='checkbox-indicator'
        className='flex items-center justify-center'
      >
        <Check className={size === 'sm' ? 'size-2.5' : 'size-3'} strokeWidth={3.5} />
      </BaseCheckbox.Indicator>
    </BaseCheckbox.Root>
  );
}
